<template>
  <div class="team-effectiveness-questionnaire" v-if="doc" style="width: 100%">
    <div class="row">
      <div
        class="col-sm-12"
        :class="isCherishQuestion ? 'cherish' : isChangeQuestion ? 'change' : ''"
        v-html="questionGuidanceText()"
        style="margin-bottom: 10px"
      ></div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <div
          v-if="isRatingQuestion"
          class="question"
          :class="questionClass(questionIndex)"
          :key="questionIndex"
        >
          <h3 class="question-text text-center">
            {{ questionTitle(questionIndex) }}
          </h3>
          <div style="height: 60px"></div>
          <div class="answer" style="width: 100%">
            <vue-slider
              ref="slider"
              :min="1"
              :max="10"
              :interval="1"
              :lazy="true"
              :piecewise="true"
              :speed="0.3"
              :tooltip="showTooltip ? 'always' : 'none'"
              :dot-size="40"
              :marks="true"
              :mark-size="20"
              :absorb="true"
              v-model="currentAnswer"
            >
            </vue-slider>

            <div class="scale" v-if="!useAlternateScale(questionIndex)">
              <span
                >Strongly <br />
                disagree</span
              >
              <span>Disagree</span>
              <span>Agree</span>
              <span
                >Strongly <br />
                agree</span
              >
            </div>
            <div class="scale" v-else>
              <span>Not close </span>
              <span></span>
              <span></span>
              <span>Very close</span>
            </div>
          </div>
        </div>

        <div class="question" key="cherish" v-if="isCherishQuestion">
          <div style="height: 20px"></div>
          <ul
            class="ter-pick-answers"
            style="width: 100%"
            :class="cherishAnswerId ? 'selection-made' : 'no-selection'"
          >
            <li
              @click="selectCherishAnswer(question.id)"
              @keydown.space="selectCherishAnswer(question.id)"
              v-for="(question, index) in pickOptions"
              :key="question.id"
              :class="[questionClass(index), { selected: cherishAnswerId == question.id }]"
              :data-id="question.id"
            >
              <div class="ter-pick-answer">
                <span
                  class="ter-pick-answer-checkbox hidden-xs glyphicon"
                  :class="
                    cherishAnswerId == question.id ? 'glyphicon-check' : 'glyphicon-unchecked'
                  "
                ></span>
                <span class="ter-pick-answer-text" v-html="cms.asText(question.text)"></span>
              </div>
            </li>
          </ul>
          <div class="pick-question-image">
            <img src="../../assets/team-effectiveness/otter-gif-2.gif" alt="" />
          </div>
        </div>

        <div class="question" key="change" v-if="isChangeQuestion">
          <div style="height: 20px"></div>
          <ul
            class="ter-pick-answers"
            style="width: 100%"
            :class="changeAnswerId ? 'selection-made' : 'no-selection'"
          >
            <li
              @click="selectChangeAnswer(question.id)"
              @keydown.space="selectChangeAnswer(question.id)"
              v-for="(question, index) in pickOptions"
              :key="question.id"
              :class="[questionClass(index), { selected: changeAnswerId == question.id }]"
              :data-id="question.id"
            >
              <div class="ter-pick-answer">
                <span
                  class="ter-pick-answer-checkbox hidden-xs glyphicon"
                  :class="changeAnswerId == question.id ? 'glyphicon-check' : 'glyphicon-unchecked'"
                ></span>
                <span class="ter-pick-answer-text" v-html="cms.asText(question.text)"></span>
              </div>
            </li>
          </ul>
          <div class="pick-question-image">
            <img src="../../assets/team-effectiveness/chameleon-gif-2.gif" alt="" />
          </div>
        </div>

        <div class="ter-bottom-nav clearfix">
          <button
            class="btn btn-primary btn-lg"
            style="margin: 10px; float: left; margin-left: 0"
            v-if="canPrev"
            @click="prev()"
            type="button"
          >
            Prev
          </button>
          <button
            v-if="!isLastQuestion"
            class="btn btn-primary btn-lg"
            style="margin: 10px; float: right; margin-right: 0"
            :disabled="!canNext"
            @click="next()"
            type="button"
          >
            Next
          </button>
          <button
            v-if="isLastQuestion"
            class="btn btn-primary btn-lg"
            style="margin: 10px; float: right; margin-right: 0"
            :disabled="!canSubmit"
            @click="submit()"
            type="button"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, triggerRef } from "vue";
import VueSlider from "vue-3-slider-component";
import cms from "../../services/cms/cms";
import {
  CmsDocumentTypes,
  CmsRichText,
  CmsTeamEffectivenessReview,
} from "../../services/cms/cms-type-definitions";
import cmsContentMap from "../../services/cms/cms-content-map";
import { TeamEffectivenessRatingsApiModel } from "../../api-models";

// import "vue-slider-component/dist-css/vue-slider-component.css";
// import "vue-slider-component/theme/default.css";

const props = defineProps<{
  isSubmitting: boolean;
}>();

const emit = defineEmits(["submit"]);

const questionIndex = ref(0);

const answers = ref([5.5]);

const doc = ref<CmsTeamEffectivenessReview | null>(null);

const cherishAnswerId = ref<string | null>(null);

const changeAnswerId = ref<string | null>(null);

function useAlternateScale(index: number): boolean {
  return doc.value?.data.body[index].primary.question_id === "Potential";
}

function questionTitle(index: number): string {
  return cms.asText(doc.value?.data.body[index].primary.question_title);
}

function questionClass(index: number): string {
  const question_id = doc.value?.data.body[index].primary.question_id;
  if (question_id) {
    return `ter-${doc.value?.data.body[index].primary.question_id.toLowerCase()}`;
  }

  return "";
}

function cherishHtml(): string {
  return cms.asHtml(doc.value?.data.cherish_question_text);
}

function changeHtml(): string {
  return cms.asHtml(doc.value?.data.change_question_text);
}

function ratingQuestionHtml(): string {
  return cms.asHtml(doc.value?.data.rating_question_text);
}

const pickOptions = computed(() => {
  return doc.value?.data.body
    .filter(
      // This line removes the final question from the pick options.
      (q) => !!q.primary.question_id && q.primary.question_id !== "Potential",
    )
    .map((q) => {
      return {
        id: q.primary.question_id,
        text: q.primary.question_title,
      };
    });
});

const currentAnswer = computed({
  get: () => answers.value[questionIndex.value],
  set: (value: number) => {
    answers.value[questionIndex.value] = value;
    triggerRef(answers);
  },
});

const showTooltip = computed(() => answers.value[questionIndex.value] !== 5.5);

const canPrev = computed(() => questionIndex.value > 0);

function prev(): void {
  if (canPrev.value) {
    questionIndex.value -= 1;
  }
}

const isLastQuestion = computed(() => {
  return questionIndex.value === answers.value.length - 1 + 2;
});

const canSubmit = computed(() => {
  return isLastQuestion.value && !!changeAnswerId.value && !props.isSubmitting;
});

const isRatingQuestion = computed(() => {
  return questionIndex.value <= answers.value.length - 1;
});

const isPickingQuestion = computed(() => {
  return questionIndex.value > answers.value.length - 1;
});

const isCherishQuestion = computed(() => {
  return questionIndex.value === answers.value.length - 1 + 1;
});

const isChangeQuestion = computed(() => {
  return questionIndex.value === answers.value.length - 1 + 2;
});

function selectCherishAnswer(questionId: string): void {
  cherishAnswerId.value = questionId === cherishAnswerId.value ? null : questionId;
}

function selectChangeAnswer(questionId: string): void {
  changeAnswerId.value = questionId === changeAnswerId.value ? null : questionId;
}

function questionGuidanceText(): string {
  if (isCherishQuestion.value) {
    return cherishHtml();
  }
  if (isChangeQuestion.value) {
    return changeHtml();
  }
  return ratingQuestionHtml();
}

const canNext = computed(() => {
  return (
    (!isLastQuestion.value &&
      isRatingQuestion.value &&
      answers.value[questionIndex.value] !== 5.5) ||
    (isCherishQuestion.value && !!cherishAnswerId.value) ||
    (isChangeQuestion.value && !!changeAnswerId.value)
  );
});

function next(): void {
  if (canNext.value) {
    questionIndex.value += 1;
  }
}

function submit(): void {
  if (canSubmit.value) {
    // Create a dictionary/hash of question IDs/answers
    const ratings = doc.value?.data.body.reduce((obj, question, index) => {
      obj[question.primary.question_id] = answers.value[index];
      return obj;
    }, {});
    const rawRatings = {
      ratings,
      cherishQuestionId: cherishAnswerId.value,
      changeQuestionId: changeAnswerId.value,
    } as TeamEffectivenessRatingsApiModel;
    emit("submit", rawRatings);
  }
}

onMounted(() => {
  cms
    .getById(
      CmsDocumentTypes.CmsTeamEffectivenessReview,
      cmsContentMap.teamEffectiveness.questionnaireQuestions,
    )
    .then((d) => {
      doc.value = d;
      answers.value = d.data.body.map(() => 5.5);
    });
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.cherish h3 strong {
  color: @charismatic-color !important;
}

.change h3 strong {
  color: @transformational-color !important;
}

.team-effectiveness-questionnaire {
  background-color: #fff;
  position: relative;

  .vue-slider-mark-label {
    display: none;
  }

  @sliderSize: 6px;

  .vue-slider {
    height: @sliderSize !important;
  }

  .vue-slider-mark {
    width: @sliderSize !important;
    height: @sliderSize !important;
  }

  // height: 100vh;
  .ter-bottom-nav {
    width: 100%;
    position: relative;
  }

  .question {
    min-height: 27rem;
  }

  .question-text {
    color: white;
    margin: 0;
    padding: 25px 15px;
  }

  .scale {
    margin: 0 auto;
    margin-top: 5px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    // padding-left: 20px;
    // padding-right: 20px;

    & > span {
      display: block;
      width: 8em;
      text-align: center;

      &:first-child {
        text-align: left;
      }

      &:last-child {
        text-align: right;
      }
    }
  }

  .vue-slider-dot-handle-focus {
    box-shadow: 0px 0px 1px 2px;
  }

  .vue-slider-process,
  .vue-slider-dot-tooltip-inner,
  .question-text,
  .ter-pick-answer {
    background-color: @professional-color;
    border-color: @professional-color;
  }

  .vue-slider-dot-handle-focus {
    color: fade(@professional-color, 36%);
  }

  .ter-innovator {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @innovator-color;
      border-color: @innovator-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@innovator-color, 36%);
    }
  }

  .ter-strategist {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @strategist-color;
      border-color: @strategist-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@strategist-color, 36%);
    }
  }

  .ter-transformational {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @transformational-color;
      border-color: @transformational-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@transformational-color, 36%);
    }
  }

  .ter-professional {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @professional-color;
      border-color: @professional-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@professional-color, 36%);
    }
  }

  .ter-entrepreneurial {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @entrepreneurial-color;
      border-color: @entrepreneurial-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@entrepreneurial-color, 36%);
    }
  }

  .ter-charismatic {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @charismatic-color;
      border-color: @charismatic-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@charismatic-color, 36%);
    }
  }

  .ter-servant {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @servant-color;
      border-color: @servant-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@servant-color, 36%);
    }
  }

  .ter-practical {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @practical-color;
      border-color: @practical-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@practical-color, 36%);
    }
  }

  .ter-executional {
    .vue-slider-process,
    .vue-slider-dot-tooltip-inner,
    .question-text,
    .ter-pick-answer {
      background-color: @executional-color;
      border-color: @executional-color;
    }

    .vue-slider-dot-handle-focus {
      color: fade(@executional-color, 36%);
    }
  }

  .ter-pick-answers {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-evenly;
    padding-left: 0;

    li {
      cursor: pointer;
      list-style-type: none;
      font-size: 90%;
      margin-bottom: 5px;
      line-height: 1;
      transition: opacity 0.2s;

      .ter-pick-answer {
        padding: 12px 10px;
      }

      .ter-pick-answer-text {
        color: white;
        display: inline-block;
        font-weight: bold;
        margin-left: 15px;
      }

      .ter-pick-answer-checkbox {
        vertical-align: text-top;
        font-size: 110%;
      }
    }

    &.selection-made li {
      opacity: 0.3;
    }

    &.selection-made li.selected {
      opacity: 1;
    }
  }

  .pick-question-image {
    position: relative;

    img {
      max-height: 200px;
      position: absolute;
      top: -20px;
      left: calc(50% - 100px);
    }
  }
}
</style>
